import {
  ComparatorContext,
  ComponentDescriptorOrArray,
  Filter,
  getRendererFromDescriptor,
  getTypeFromQueryParams,
  IProduct,
  KiziLogger,
  options,
  parseBool,
  ProductType,
  useCurrentStepIndex,
  useField,
  useFields,
  usePage,
  useWizardService,
} from '@kizi/comparator-core';
import _ from 'lodash';
import {
  default as React,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { addFiltersToProducts, filterProducts } from '../../../utils/filters';
import { isMobile, useCoveragesWithValue } from '../../../utils/global';
import { getProductsWithReviews } from '../../../utils/product';
import { AffiliateUrlModal } from '../../AffiliateUrlModal';
import {
  CompareSelect,
  CompareSelectProps,
} from '../../comparison/CompareSelect';
import { Choices, FiltersProps } from '../../Filters';
import { useReviews } from '../../hooks/reviews';
import { PageLayout, PageLayoutProps } from '../../PageLayout';
import { OverviewPageLoader } from '../../Pages/OverviewPageLoader';
import { SortingOption } from '../../Sorting';
import { sendCustomEvent } from '../TabViewWizard/ApplyPageWizard';
import {
  handleTopThree3P,
  handleTopThreeCb3P,
  handleTopThreeHealth,
} from '../../../utils/helpers';
import { useCompareTracking, useProductSorting } from '../../../utils/hooks';

export interface OverviewPageNewProps extends PageLayoutProps {
  filters: Filter[];
  sortingOptions: SortingOption[];
  sortingReference?: string[];
  sortingDescendingOptions?: boolean[];
  sortingDependenceFieldName?: string;
  productsRequest: {
    url: string;
    options?: {
      method?: string;
      headers?: any;
      body?: any;
      queryParams?: { [key: string]: string };
    };
  };
  selectedFilters: FiltersProps['choices'];
  filtersToIgnore: string[];
  filtersToListen?: string[];
  selectedSorting: number;
  paramsFromFields: string[];
  defaultQueryParams?: {
    limit?: number;
    tariff?: string;
    sustainable?: string;
    housenumberaddition?: string;
    electricitycapacity?: number;
    gascapacity?: number;
    smartmeter?: number;
    customertype?: string;
    supplytype?: string;
    withsavings?: number;
    pricesexcludingtax?: boolean;
    hideunavailableproducts?: boolean;
    type?: string;
  };
  dynamicField?: string;
  affiliateUrlClientId?: string;
  affiliatePageDescriptor?: ComponentDescriptorOrArray;
  registerAnalyticsViewListEvent?;
  sortType?: string;
  withTopThree?: boolean;
  customLoadingPage?: Object;
}

export const OverviewPageNew: React.FC<OverviewPageNewProps> = ({
  filters,
  sortingOptions,
  sortingReference = [],
  sortingDescendingOptions = [],
  productsRequest,
  selectedSorting,
  content,
  sideNav,
  selectedFilters: _selectedFilters = {},
  defaultQueryParams = {},
  paramsFromFields,
  filtersToListen = [],
  filtersToIgnore = [],
  dynamicField = '',
  affiliateUrlClientId = 'consumentenbond',
  affiliatePageDescriptor,
  registerAnalyticsViewListEvent,
  sortType = '',
  withTopThree = false,
  customLoadingPage,
  ...props
}) => {
  const getSorting = () => {
    const savedSorting = sessionStorage.getItem('sorting');
    if (savedSorting) {
      return parseInt(savedSorting);
    }

    return selectedSorting;
  }

  const { state, setState } = usePage();
  const [currentSorting, setCurrentSorting] =
    useState(getSorting());
  const [topThree, setTopThreeProducts] = useState<any[]>([]);
  const [selectedFilters, setSelectedFilters] = useState(_selectedFilters);
  const fields = useFields(paramsFromFields ?? []);
  const [visibleProducts, setVisibleProducts] = useState<IProduct[]>([]);
  const isLoadingProducts = useField('overview.isLoadingProducts');
  const isLoadingPage = parseBool(isLoadingProducts?.value);
  const userEmail = useField('getUserInput.optionalemail');
  const sortTypeField = useField(sortType as string);
  const { trackCompareClick } = useCompareTracking();
  const fieldsValue = fields.map((field) => {
    return field?.value;
  });

  const {
    getStateFromLocalStorage,
    api,
    persistStateInLocalStorage,
    name: pageName,
    isPartner,
  } = useContext(ComparatorContext);
  const { reviews, isLoading } = useReviews(
    (defaultQueryParams?.type ||
      getTypeFromQueryParams(paramsFromFields)) as ProductType,
  );

  const dynamicFieldValue = useField(dynamicField);
  const dynamicFieldFromProps = useCoveragesWithValue(dynamicFieldValue);

  const currentStepIndex = useCurrentStepIndex();
  const service = useWizardService();
  const filtersField = useField('overview.filters');
  const dynamicFiltersToListenField = useField(
    'overview.dynamicFiltersToListen',
  );
  const requestLogField = useField('overview.requestLogId');
  const filtersValues = JSON.parse(
    filtersField?.value ? (filtersField.value as string) : '[]',
  );

  if (filtersToListen.length === 0)
    filtersToListen = JSON.parse(
      (dynamicFiltersToListenField?.value as string) || '[]',
    );

  const apiFilters = filtersToListen.map((filterName) => {
    const filtersValue = JSON.parse(filtersField?.value as string);
    const filterValue =
      Array.isArray(filtersValue[filterName]) &&
      filtersValue[filterName].length > 0
        ? filtersValue[filterName][0]
        : '';

    //because of the maxOwnRisk causing API call when you select or deselect Aanvullende dekking
    if (filterValue !== '' && filterValue !== selectedFilters[filterName]) {
      return filterValue;
    }

    return selectedFilters[filterName];
  });

  const comparisonProductsField = useField('overview.comparisonProducts');
  const comparisonProducts = JSON.parse(
    comparisonProductsField?.value
      ? (comparisonProductsField?.value as string)
      : '[]',
  );
  const fieldValueChange = (name: string, value: string) => {
    value &&
      name &&
      name.length > 0 &&
      service?.send({
        type: 'CHANGE',
        target: { name: name, value: value },
      });
  };

  const generateSortingOptions = () => {
    if (!sortingOptions) return [];
    return sortingOptions.filter(({ dependsOn }) => {
      if (dependsOn) {
        return filtersValues[dependsOn.dependenceProperty]?.includes(
          dependsOn.dependenceValue,
        );
      }
      return true;
    });
  };

  const loadComparisonProducts = (products: IProduct[]) => {
    if (comparisonProducts && comparisonProducts.length > 0) {
      const tempProducts: IProduct[] =
        isMobile() && comparisonProducts.length > 2
          ? comparisonProducts.slice(0, comparisonProducts.length - 1)
          : comparisonProducts;

      tempProducts.forEach((p) => {
        const productIndex = products.findIndex((f) => f.id === p.id);
        if (productIndex >= 0) {
          products[productIndex].compare = true;
        }
      });
    }
  };

  const checkInactivityEmail = async (requestLogId, params, fieldFilters) => {
    const storedParams = localStorage.getItem('queryParams') || '';
    const storedFilters = localStorage.getItem('queryFilters') || '';

    if (storedParams === '' || storedFilters === '') {
      localStorage.setItem('queryParams', JSON.stringify(params));
      localStorage.setItem('queryFilters', JSON.stringify(fieldFilters));
      await sendEmailToCustomer(requestLogId || '');
      return;
    }
    if (
      !_.isEqual(JSON.parse(storedParams), params) ||
      !_.isEqual(JSON.parse(storedFilters), fieldFilters)
    ) {
      localStorage.setItem('queryParams', JSON.stringify(params));
      localStorage.setItem('queryFilters', JSON.stringify(fieldFilters));

      await sendEmailToCustomer(requestLogId || '');
    }
  };

  const sendEmailToCustomer = async (requestLogId) => {
    if (userEmail?.value) {
      await api.sendEmailToCustomer(
        requestLogId || '',
        userEmail.value.toString(),
        ProductType[pageName.toUpperCase()],
        true,
      );
    }
  };

  const updateState = (setFn, currentState, data) => {
    if (setFn) {
      setFn({ ...currentState, ...data });
    }
  };

  const handleDynamicFieldFromProps = (queryParams, field, fieldFromProps) => {
    if (fieldFromProps && field?.name === dynamicField.split('.')[1]) {
      queryParams[field?.name] = fieldFromProps;
    }
  };

  useEffect(() => {
    if (service) {
      const overviewPageIndex = service.state.context.initialSteps.findIndex(
        (e) => e.name === 'overview',
      );
      setTimeout(() => {
        service?.send({
          type: 'GOTO',
          index: overviewPageIndex,
          meta: { reload: true },
        });
      }, 86400000); //auto refresh after 24 hours
    }
  }, [service]);
  useEffect(() => {
    isLoadingProducts?.set({ value: 'true' });

    const userHomeTypeField = fields.find((field) => {
      return field?.name === 'hometype' && field.formName === 'getUserInput';
    });
    const addressRequestDataField = fields.find((field) => {
      return (
        field?.name === 'addressRequestData' && field.formName === 'coverages'
      );
    });
    let addressHomeType;
    try {
      const addressRequestData = addressRequestDataField?.value
        ? JSON.parse(addressRequestDataField.value as string)
        : { homeType: 'VU' };
      addressHomeType = addressRequestData.homeType;
    } catch (error) {
      console.error('Error parsing addressRequestData:', error);
      console.log('Raw value:', addressRequestDataField?.value);
      addressHomeType = 'VU';
    }
    const queryParams = {
      ...(pageName.includes('home')
        ? { userhometype: userHomeTypeField?.value, addressHomeType }
        : {}),
    };
    for (const field of fields) {
      if (field) queryParams[field.name] = field.value ?? '';

      handleDynamicFieldFromProps(queryParams, field, dynamicFieldFromProps);
    }

    const finalQueryParams = { ...defaultQueryParams, ...queryParams };

    api
      .getProducts(finalQueryParams, filtersValues, productsRequest.options)
      .then((_productsFromApi) => {
        const filterSustainabilityScore = (products, pageName) => {
          const source =
            pageName.includes('health') ? 'vbdo' : 'eerlijkegeldwijzer';
          return products.map((product) => {
            const filteredScore =
              product.sustainabilityScore?.find?.((el) => el.source === source)
                ?.score || 0;

            return {
              ...product,
              sustainabilityScore: String(filteredScore),
              provider: {
                ...product.provider,
                sustainabilityScore: String(filteredScore),
              },
            };
          });
        };

        if (pageName.includes('health')) {
          _productsFromApi = filterSustainabilityScore(
            _productsFromApi,
            pageName,
          );
        }

        let productsFromApi = filters
          ? addFiltersToProducts(
              filters.filter((option) => !filtersToIgnore.includes(option.id)),
              _productsFromApi,
            )
          : _productsFromApi;
        if (pageName === 'os-home') {
          productsFromApi = productsFromApi.filter((product) => {
            return !['V2', 'V3'].includes(
              product?.homePackage?.underwritingCompanySpecifics[0]
                .underwritingResult || '',
            );
          });
        }

        if (productsFromApi.length > 0) {
          const requestLogId =
            productsFromApi[0].commonTechnical[0].transactionReference;
          requestLogField?.onChange?.({
            target: {
              name: 'requestLogId',
              value: requestLogId,
            },
          } as React.ChangeEvent<any>);
          sendCustomEvent('QuotationStarted', {
            requestLogId: requestLogId,
          });
          checkInactivityEmail(requestLogId, finalQueryParams, filtersValues);
          if (productsFromApi[0].commonTechnical[0]?.accessCode) {
            const clientNameField = fields.find(
              (field) => field?.name === 'clientName',
            );
            clientNameField?.set({
              value: productsFromApi[0].commonTechnical[0]?.accessCode,
            });
          }
        }

        loadComparisonProducts(productsFromApi);
        fieldValueChange('apiProducts', JSON.stringify(productsFromApi));

        if (registerAnalyticsViewListEvent) {
          const googleAnalyticsObject = registerAnalyticsViewListEvent(
            productsFromApi,
            filtersValues,
          );

          productsFromApi = productsFromApi.map((productFromApi, index) => {
            return {
              ...productFromApi,
              analyticsData: googleAnalyticsObject[index],
            };
          });
        }

        updateState(setState, state, {
          availableFilters: api.getFilters(productsFromApi, filters),
          onChange: handleChange(productsFromApi),
          onItemClick: (selectedSorting) => {
            setCurrentSorting(selectedSorting);
            setVisibleProducts(filterProducts(productsFromApi, filters as any));
          },
          onCompareChange: handleCompareChange(productsFromApi),
          options: generateSortingOptions(),
          selected: currentSorting,
        });
        isLoadingProducts?.set({ value: 'false' });

        const encodedFilters = JSON.stringify(selectedFilters);
        const allFields = service?.children.get('overview')?.getSnapshot()
          .context.fields;
        const indexFilters = allFields?.findIndex((f) => f.name === 'filters');
        const decodedFilters =
          indexFilters !== undefined && indexFilters > -1
            ? JSON.parse(allFields?.[indexFilters]?.value as string)
            : JSON.parse(filtersField?.value as string);

        if (Object.keys(decodedFilters).length > 0) {
          setSelectedFilters(decodedFilters);
          setVisibleProducts(filterProducts(productsFromApi, decodedFilters));
          if (Object.keys(decodedFilters).length === 1) {
            service?.send?.([
              {
                type: 'CHANGE',
                target: { name: 'filters', value: encodedFilters },
              },
              {
                type: 'CHANGE',
                target: { name: 'defaultFilters', value: encodedFilters },
              },
            ]);
          }
        } else {
          setVisibleProducts(productsFromApi);
        }
        if (productsFromApi.length > 5 && withTopThree) {
          if (pageName.includes('health')) {
            setTopThreeProducts(handleTopThreeHealth(productsFromApi));
          }

          if (pageName === 'os-triple-play') {
            setTopThreeProducts(handleTopThree3P(productsFromApi));
          }

          if (pageName === 'triple-play') {
            setTopThreeProducts(handleTopThreeCb3P(productsFromApi));
          }
        }
      })
      .catch((error) => {
        KiziLogger.logError({
          eventId: 0,
          member: `OverviewPageNew - ${pageName}`,
          message: 'Error in useEffect',
          eventName: 'getProducts',
          data: {
            error,
            variables: {
              finalQueryParams,
              filtersValues,
              options: productsRequest.options,
            },
          },
        });
        isLoadingProducts?.set({ value: 'false' });
      });
  }, [JSON.stringify(fieldsValue), JSON.stringify(apiFilters)]);

  useEffect(() => {
    updateState(setState, state, { options: generateSortingOptions() });
  }, [options]);

  useEffect(() => {
    updateState(setState, state, { selected: currentSorting });
  }, [currentSorting]);

  const productsWithReviews: IProduct[] = useMemo(() => {
    if (isLoading || !reviews) {
      return visibleProducts;
    } else {
      return getProductsWithReviews(visibleProducts, reviews);
    }
  }, [visibleProducts, isLoading, reviews]);

  useEffect(() => {
    updateState(setState, state, { topThree: topThree });
  }, [topThree]);

  useProductSorting(
    productsWithReviews,
    currentSorting,
    sortTypeField,
    sortingReference,
    sortingDescendingOptions,
  );

  const selectedPriceMode = useMemo(
    () => selectedFilters['showPriceMode']?.[0] as 'month' | 'year',
    [JSON.stringify(selectedFilters)],
  );

  let interpreter;

  const initializeInterpreter = () => {
    if (
      !service ||
      !service.state.context.steps ||
      currentStepIndex === undefined
    ) {
      return null;
    }
    return service.state.context.steps[currentStepIndex];
  };
  interpreter = initializeInterpreter();

  useEffect(() => {
    if (!interpreter || !currentStepIndex) return;

    const subscription = interpreter.subscribe((s) => {
      if (
        s.event.type === 'done.invoke.onChangeAsynchronousValidation' ||
        s.event.type === 'SET'
      ) {
        const persistedState = getStateFromLocalStorage();
        if (!persistedState || !persistedState.context) return;

        persistedState.context.initialSteps[currentStepIndex].fields =
          s.context.fields;
        persistStateInLocalStorage(persistedState);
      }
    });

    return () => subscription.unsubscribe();
  }, [interpreter, currentStepIndex]);

  if (!setState || !service) return null;

  const handleChange = (products) => (values: Choices) => {
    const encodedData = JSON.stringify(values);

    const _filters = interpreter
      .getSnapshot()
      ?.context.fields.findIndex((field) => field.name === 'filters');
    if (_filters !== undefined && _filters > -1) {
      service?.send?.([
        {
          type: 'CHANGE',
          target: { name: 'filters', value: encodedData },
        },
      ]);
    }

    setSelectedFilters(values);
    setVisibleProducts(filterProducts(products, values));
  };

  const handleCompareChange =
    (products) =>
    (
      e: React.ChangeEvent<HTMLInputElement>,
      id: string,
      component = 'product',
    ) => {
      const productIndex = products.findIndex((product) => product.id === id);
      const selectedProducts = products.filter(function (product) {
        return product.compare === true;
      });

      const allowedLength = isMobile() ? 1 : 2;

      if (productIndex >= 0) {
        if (
          selectedProducts.length > allowedLength &&
          products[productIndex].compare === false
        )
          return;
        products[productIndex].compare =
          e != null ? e.target.checked : !products[productIndex].compare;

        trackCompareClick(products[productIndex], component);
      }

      const saveCompareFieldValues = products.filter(function (product) {
        return product.compare === true;
      });

      fieldValueChange(
        'comparisonProducts',
        JSON.stringify(saveCompareFieldValues),
      );

      const allFields = interpreter.getSnapshot()?.context.fields;
      const indexFilters = allFields?.findIndex((f) => f.name === 'filters');
      if (indexFilters !== undefined && indexFilters > -1) {
        const decodedFilters = JSON.parse(
          allFields?.[indexFilters]?.value as string,
        );

        setVisibleProducts(filterProducts(products, decodedFilters));
      }
    };

  const isAffiliateUrl = location.href.includes('affiliateUrl');

  const compareSelectProps: CompareSelectProps = {
    products: productsWithReviews,
    priceMode: selectedPriceMode,
    setProducts: setVisibleProducts,
    isIProducts: true,
  };

  const handleAffiliateUrlModal = ({
    affiliatePageDescriptor,
    affiliateUrlClientId,
  }) => {
    return affiliatePageDescriptor ? (
      <>
        {getRendererFromDescriptor({
          ...affiliatePageDescriptor,
          props: {
            ...affiliatePageDescriptor.props,
            affiliateUrlClientId,
          },
        })}
      </>
    ) : (
      <AffiliateUrlModal affiliateUrlClientId={affiliateUrlClientId} />
    );
  };

  return (
    <>
      {isAffiliateUrl ? (
        handleAffiliateUrlModal({
          affiliatePageDescriptor,
          affiliateUrlClientId,
        })
      ) : (
        <>
          {isLoadingPage ? (
            <OverviewPageLoader
              isOs={
                pageName.includes('os-') || pageName === 'moneysure-event-based'
              }
              pageName={pageName}
              customContent={customLoadingPage}
              isPartner={isPartner}
            />
          ) : (
            <PageLayout {...props} content={content} sideNav={sideNav} />
          )}
          <CompareSelect {...compareSelectProps} />
        </>
      )}
    </>
  );
};
